import { apiClient } from './api.service';

export interface Tenant {
  id: string;
  name: string;
  domain: string;
  status: 'active' | 'inactive';
  clioConnectionStatus: 'connected' | 'not_connected' | 'expired';
  lastConnected?: string;
  createdAt: string;
}

export interface TenantDetail {
  id: string;
  name: string;
  domain: string;
  status: 'active' | 'inactive';
  createdAt: string;
  updatedAt: string;
  clioConfig: {
    status: 'connected' | 'not_connected' | 'expired';
    clientId?: string;
    lastConnected?: string;
    tokenExpiresAt?: string;
    hasRefreshToken: boolean;
    webhookUrls?: {
      [key: string]: {
        enabled: boolean;
        url: string;
      };
    };
  };
  configuration: {
    emailNotifications: boolean;
    pdfGeneration: boolean;
    webhookEnabled: boolean;
  };
  logs: Array<{
    id: string;
    timestamp: string;
    level: 'info' | 'warning' | 'error';
    message: string;
    source: string;
  }>;
}

// API Response interfaces matching backend
interface AdminTenantListResponse {
  tenants: Array<{
    tenantId: string;
    tenantName: string;
    status: string;
    createdAt: string;
    primaryDomain?: string;
    adminEmail?: string;
    lastActivity?: string;
    features: {
      clioIntegration: boolean;
      emailNotifications: boolean;
      documentArchival: boolean;
    };
    clioConfig: {
      connected: boolean;
      lastSync?: string;
    };
  }>;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface AdminTenantDetailResponse {
  tenantId: string;
  tenantName: string;
  alias?: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  primaryDomain?: string;
  domains: string[];
  contactInfo: {
    adminEmail: { address: string; validated: boolean };
    techEmail: { address: string; validated: boolean };
    opsEmail: { address: string; validated: boolean };
    juniperFromEmail: { address: string; verified: boolean };
    juniperBccEmail: { address: string; validated: boolean };
  };
  clioConfig: {
    clientId?: string;
    baseUrl: string;
    webhooksConfigured: boolean;
    webhookUrls: { [key: string]: { enabled: boolean; url: string } };
    connected: boolean;
    lastSync?: string;
    tokenExpiresAt?: string;
  };
  features: {
    clioIntegration: boolean;
    emailNotifications: boolean;
    documentArchival: boolean;
    juniperEnabled: boolean;
  };
  modules: {
    [moduleName: string]: {
      enabled: boolean;
      configured: boolean;
      lastChecked?: string;
    };
  };
}

export interface CreateTenantData {
  name: string;
  domain: string;
  adminEmail: string;
}

export interface ClioTokenStatus {
  isValid: boolean;
  expiresAt?: string;
  hasRefreshToken: boolean;
  lastRefreshed?: string;
}

class TenantService {


  /**
   * Helper method to determine Clio connection status from list data
   */
  private getClioConnectionStatusFromList(clioConfig: { connected: boolean; lastSync?: string }): 'connected' | 'not_connected' | 'expired' {
    if (!clioConfig.connected) {
      return 'not_connected';
    }

    // If connected but no recent sync, might be expired
    if (clioConfig.lastSync) {
      const lastSync = new Date(clioConfig.lastSync);
      const now = new Date();
      const hoursSinceLastSync = (now.getTime() - lastSync.getTime()) / (1000 * 60 * 60);

      // Consider expired if no sync in the last 24 hours
      if (hoursSinceLastSync > 24) {
        return 'expired';
      }
    }

    return 'connected';
  }

  /**
   * Helper method to determine Clio connection status from detailed config
   */
  private getClioConnectionStatusFromConfig(clioConfig: AdminTenantDetailResponse['clioConfig']): 'connected' | 'not_connected' | 'expired' {
    if (!clioConfig.connected) {
      return 'not_connected';
    }

    // If connected but no recent sync, might be expired
    if (clioConfig.lastSync) {
      const lastSync = new Date(clioConfig.lastSync);
      const now = new Date();
      const hoursSinceLastSync = (now.getTime() - lastSync.getTime()) / (1000 * 60 * 60);

      // Consider expired if no sync in the last 24 hours (this is configurable)
      if (hoursSinceLastSync > 24) {
        return 'expired';
      }
    }

    return 'connected';
  }

  /**
* Get all tenants with basic information
*/
  async getAllTenants(): Promise<Tenant[]> {
    try {
      const response = await apiClient.get<AdminTenantListResponse>('/admin/tenants');

      // Transform API response to frontend format
      return response.data.tenants.map((apiTenant) => ({
        id: apiTenant.tenantId,
        name: apiTenant.tenantName,
        domain: apiTenant.primaryDomain || '',
        status: apiTenant.status as 'active' | 'inactive',
        clioConnectionStatus: this.getClioConnectionStatusFromList(apiTenant.clioConfig),
        lastConnected: apiTenant.lastActivity,
        createdAt: apiTenant.createdAt,
      }));
    } catch (error) {
      console.error('Failed to fetch tenants:', error);
      throw new Error('Failed to load tenants');
    }
  }

  /**
* Get detailed information for a specific tenant
*/
  async getTenantDetail(tenantId: string): Promise<TenantDetail> {
    try {
      const response = await apiClient.get<AdminTenantDetailResponse>(`/admin/tenants/${tenantId}`);

      // Transform API response to frontend format
      const apiTenant = response.data;
      const clioStatus = this.getClioConnectionStatusFromConfig(apiTenant.clioConfig);

      return {
        id: apiTenant.tenantId,
        name: apiTenant.tenantName,
        domain: apiTenant.primaryDomain || apiTenant.domains[0] || '',
        status: apiTenant.status as 'active' | 'inactive',
        createdAt: apiTenant.createdAt,
        updatedAt: apiTenant.updatedAt,
        clioConfig: {
          status: clioStatus,
          clientId: apiTenant.clioConfig.clientId,
          lastConnected: apiTenant.clioConfig.lastSync,
          tokenExpiresAt: apiTenant.clioConfig.tokenExpiresAt,
          hasRefreshToken: apiTenant.clioConfig.connected, // Assume if connected, has refresh token
          webhookUrls: apiTenant.clioConfig.webhookUrls,
        },
        configuration: {
          emailNotifications: apiTenant.features.emailNotifications,
          pdfGeneration: apiTenant.modules.pdfTemplates?.configured || false,
          webhookEnabled: apiTenant.clioConfig.webhooksConfigured,
        },
        logs: [], // Logs not available in current API - would need separate endpoint
      };
    } catch (error) {
      console.error('Failed to fetch tenant detail:', error);
      throw new Error('Failed to load tenant details');
    }
  }

  /**
   * Create a new tenant
   */
  async createTenant(data: CreateTenantData): Promise<Tenant> {
    try {
      // For now, return mock response until API is ready
      await new Promise(resolve => setTimeout(resolve, 1500));

      const newTenant: Tenant = {
        id: `tenant-${Date.now()}`,
        name: data.name,
        domain: data.domain,
        status: 'active',
        clioConnectionStatus: 'not_connected',
        createdAt: new Date().toISOString(),
      };

      return newTenant;

      // TODO: Replace with actual API call when ready
      // const response = await apiClient.post<{ tenant: Tenant }>('/admin/tenants', data);
      // return response.tenant;
    } catch (error) {
      console.error('Failed to create tenant:', error);
      throw new Error('Failed to create tenant');
    }
  }

  /**
   * Update tenant configuration
   */
  async updateTenant(_tenantId: string, _data: Partial<CreateTenantData>): Promise<Tenant> {
    try {
      // For now, return mock response until API is ready
      await new Promise(resolve => setTimeout(resolve, 1000));

      throw new Error('Update tenant not implemented yet');

      // TODO: Replace with actual API call when ready
      // const response = await apiClient.put<{ tenant: Tenant }>(`/admin/tenants/${tenantId}`, data);
      // return response.tenant;
    } catch (error) {
      console.error('Failed to update tenant:', error);
      throw new Error('Failed to update tenant');
    }
  }

  /**
   * Delete a tenant
   */
  async deleteTenant(_tenantId: string): Promise<void> {
    try {
      // For now, return mock response until API is ready
      await new Promise(resolve => setTimeout(resolve, 1000));

      throw new Error('Delete tenant not implemented yet');

      // TODO: Replace with actual API call when ready
      // await apiClient.delete(`/admin/tenants/${tenantId}`);
    } catch (error) {
      console.error('Failed to delete tenant:', error);
      throw new Error('Failed to delete tenant');
    }
  }

  /**
* Get Clio connection status for a tenant
*/
  async getClioConnectionStatus(tenantId: string): Promise<ClioTokenStatus> {
    try {
      // For now, return mock data until API is ready
      await new Promise(resolve => setTimeout(resolve, 500));

      const mockStatuses: Record<string, ClioTokenStatus> = {
        '0000000001': {
          isValid: true,
          expiresAt: '2025-08-14T10:30:00Z',
          hasRefreshToken: true,
          lastRefreshed: '2025-08-07T10:30:00Z',
        },
        '0000000002': {
          isValid: false,
          expiresAt: '2025-07-22T14:20:00Z',
          hasRefreshToken: false,
          lastRefreshed: '2025-07-15T14:20:00Z',
        },
      };

      return mockStatuses[tenantId] || {
        isValid: false,
        hasRefreshToken: false,
      };

      // TODO: Replace with actual API call when ready
      // const response = await apiService.get<{ status: ClioTokenStatus }>(`/admin/tenants/${tenantId}/clio/status`);
      // return response.status;
    } catch (error) {
      console.error('Failed to get Clio connection status:', error);
      throw new Error('Failed to check Clio connection status');
    }
  }

  /**
* Test Clio connection for a tenant
*/
  async testClioConnection(tenantId: string): Promise<{ success: boolean; message: string; userInfo?: { name: string; email: string; accountName?: string } }> {
    try {
      const response = await apiClient.post<{ success: boolean; message: string; userInfo?: { id: string; name: string; email: string; accountName: string } }>(`/admin/tenants/${tenantId}/clio/test-connection`);
      return response.data;
    } catch (error) {
      console.error('Failed to test Clio connection:', error);
      throw new Error('Failed to test Clio connection');
    }
  }

  /**
* Refresh Clio access token for a tenant
*/
  async refreshClioToken(tenantId: string): Promise<{ success: boolean; message: string; newExpiresAt?: string }> {
    try {
      const response = await apiClient.post<{ success: boolean; message: string; newExpiresAt?: string }>(`/admin/tenants/${tenantId}/clio/refresh-token`);
      return response.data;
    } catch (error) {
      console.error('Failed to refresh Clio token:', error);
      throw new Error('Failed to refresh Clio token');
    }
  }

  /**
* Revoke Clio connection for a tenant
*/
  async revokeClioConnection(tenantId: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.post<{ success: boolean; message: string }>(`/admin/tenants/${tenantId}/clio/revoke-connection`);
      return response.data;
    } catch (error) {
      console.error('Failed to revoke Clio connection:', error);
      throw new Error('Failed to revoke Clio connection');
    }
  }

  /**
   * Get tenant activity logs
   */
  async getTenantLogs(_tenantId: string, _limit = 50): Promise<TenantDetail['logs']> {
    try {
      // For now, return mock response until API is ready
      await new Promise(resolve => setTimeout(resolve, 500));

      return []; // Mock empty logs for now

      // TODO: Replace with actual API call when ready
      // const response = await apiClient.get<{ logs: TenantDetail['logs'] }>(`/admin/tenants/${tenantId}/logs?limit=${limit}`);
      // return response.logs;
    } catch (error) {
      console.error('Failed to fetch tenant logs:', error);
      throw new Error('Failed to load tenant logs');
    }
  }

  /**
   * Update tenant configuration settings
   */
  async updateTenantConfiguration(
    _tenantId: string,
    _config: Partial<TenantDetail['configuration']>
  ): Promise<TenantDetail['configuration']> {
    try {
      // For now, return mock response until API is ready
      await new Promise(resolve => setTimeout(resolve, 800));

      throw new Error('Update tenant configuration not implemented yet');

      // TODO: Replace with actual API call when ready
      // const response = await apiClient.put<{ configuration: TenantDetail['configuration'] }>(
      //   `/admin/tenants/${tenantId}/configuration`,
      //   config
      // );
      // return response.configuration;
    } catch (error) {
      console.error('Failed to update tenant configuration:', error);
      throw new Error('Failed to update tenant configuration');
    }
  }

  /**
* Generate Clio OAuth URL for tenant
*/
  async generateClioAuthUrl(tenantId: string, clientId: string, clientSecret: string): Promise<{ authUrl: string; state: string }> {
    try {
      // Generate redirect URI based on current origin
      const redirectUri = `${window.location.origin}/auth/clio/callback`;

      const response = await apiClient.post<{ authUrl: string; state: string }>(`/admin/tenants/${tenantId}/clio/oauth/url`, {
        clientId,
        clientSecret,
        redirectUri
      });
      return response.data;
    } catch (error) {
      console.error('Failed to generate Clio auth URL:', error);
      throw new Error('Failed to generate Clio authorization URL');
    }
  }

  /**
* Handle Clio OAuth callback and exchange code for tokens
*/
  async handleClioCallback(
    tenantId: string,
    authCode: string,
    state: string,
    clientId: string,
    clientSecret: string
  ): Promise<{ success: boolean; message: string; tokenInfo?: any }> {
    try {
      const response = await apiClient.post<{ success: boolean; message: string; tokenInfo?: any }>(`/admin/tenants/${tenantId}/clio/oauth/callback`, {
        code: authCode,
        state,
        clientId,
        clientSecret
      });
      return response.data;
    } catch (error) {
      console.error('Failed to handle Clio callback:', error);
      throw new Error('Failed to complete Clio authorization');
    }
  }
}

export const tenantService = new TenantService();
